import React, { useLayoutEffect, useState } from 'react';
import { Theme } from '@material-ui/core/styles';
import {
  AppBar,
  Toolbar,
  IconButton,
  makeStyles,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Typography,
  ListSubheader,
  Collapse,
  colors,
} from '@material-ui/core';
import {
  Menu as MenuIcon,
  ChevronLeft,
  BarChart,
  People,
  Dashboard,
  Assignment,
  Timer,
  CreditCard,
  AttachMoney,
  ExpandLess,
  ExpandMore,
  BusinessCenter,
  PermMedia,
  AccountBalance,
  Settings,
  CheckBox,
  Home,
  Event as EventIcon,
  Today,
  Receipt,
  LocalShipping,
  LocalAtm,
} from '@material-ui/icons';
import clsx from 'clsx';
import AppBarContext, { AppBarContextData } from '../../contexts/AppBarContext';
import { useLocation } from 'react-router-dom';
import FileDialog from '../files/FileDialog';
import SettingsDialog from './SettingsDialog';
import { useEffect } from 'react';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import { useToast } from '../../hooks/useToast';

export const globalAny: any = global;
export const drawerWidth = 250;
const useStyles = makeStyles((theme: Theme) => ({
  grow: {
    flexGrow: 1,
    height: '100%',
    color: 'white',
    overflowX: 'hidden',
  },
  rightMenuItem: {
    margin: 'auto',
    marginRight: -theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  rightMenuMarginAuto: {
    marginLeft: 'auto',
  },
  appBar: {
    backgroundColor: theme.palette.primary.main,
    color: 'inherit',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    zIndex: 1200,
  },
  appBarShift: {
    [theme.breakpoints.down('sm')]: {
      width: `100%`,
    },
    [theme.breakpoints.up('md')]: {
      width: `calc(100vw - ${drawerWidth}px)`,
    },
    boxSizing: 'border-box',
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : colors.blue[50],
    width: drawerWidth,
  },
  content: {
    padding: 0,
    display: 'flex',
    overlfow: 'auto',
    transition: theme.transitions.create(['margin', 'padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('sm')]: {
      marginLeft: -drawerWidth,
      marginRight: 0,
      paddingLeft: drawerWidth,
    },
  },
  contentShift: {
    transition: theme.transitions.create(['margin', 'padding'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: -drawerWidth,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: drawerWidth,
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },
  footerList: {
    paddingTop: 0,
  },
  mainList: {
    overflowY: 'auto',
  },
  selectedListItemRoot: {
    '&$selected': {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  selected: {},
  secondary: {
    color: theme.palette.secondary.main,
  },
  userName: {
    display: 'inline-block',
    fontWeight: 'bold',
    marginLeft: 'auto',
    marginRight: 0,
  },
  pageTitle: {
    margin: 'auto 5px auto 4px',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  },
  darkModeToggle: {
    marginLeft: 0,
    marginRight: 'auto',
  },
}));

export const SideBarListItem = ({
  list,
  item,
  goToLocation,
  location,
  closeParent,
  i,
}: {
  list: any[];
  item: any;
  goToLocation?: Function;
  location?: any;
  closeParent?: Function;
  i: number;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const classes = useStyles();

  const onClick = (item) => (e) => {
    if (item?.location) goToLocation?.(item?.location);
    else {
      item?.onClick?.();
      closeParent?.();
    }

    if (item.options) setOpen((o) => !o);
  };

  const closeOptions = () => setOpen(false);

  let itemLocation = item.matchLocation ?? item.location;
  return item.divider ? (
    <React.Fragment key={i}>
      {item.hideDivider ? null : <Divider />}
      {item.subHeaderText ? <ListSubheader disableSticky>{item.subHeaderText}</ListSubheader> : null}
    </React.Fragment>
  ) : (
    <React.Fragment key={i}>
      <ListItem
        selected={location && itemLocation === location?.pathname}
        button={item.button ?? true}
        key={i}
        onClick={onClick(item)}
        classes={{ root: classes.selectedListItemRoot, selected: classes.selected }}
      >
        {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
        <ListItemText primary={item.text} />
        {item.options && !item.closeDropdown ? open ? <ExpandLess /> : <ExpandMore /> : null}
      </ListItem>
      {item.options && !item.closeDropdown ? (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <SidebarList list={item.options} goToLocation={goToLocation} location={location} closeParent={item.closeOnClick ? closeOptions : undefined} />
        </Collapse>
      ) : null}
    </React.Fragment>
  );
};

export const SidebarList = (props: { list: any[]; goToLocation?: Function; location?: any; closeParent?: Function }) => {
  return (
    <React.Fragment>
      {props.list
        ?.filter((item) => !item.hidden ?? true)
        .map((item, i) => (
          <SideBarListItem key={'listItem' + i} {...props} item={item} i={i} />
        ))}
    </React.Fragment>
  );
};

export default function NavbarWithSidebar(props) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const localDrawerOpen = localStorage.getItem('drawerOpen');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(localDrawerOpen === 'true' ? true : localDrawerOpen === 'false' ? false : !isMobile && props.isAuthenticated);
  localStorage.setItem('drawerOpen', drawerOpen?.toString());

  const location = useLocation();
  const dialogType: 'files' | 'settings' | undefined = (location.state as any)?.dialogType;
  const filesOpen = dialogType === 'files';
  const settingsOpen = dialogType === 'settings';

  const [employeeInfo] = useLoggedInUser();
  const toast = useToast();

  const [appBarData, setAppBarData] = useState<AppBarContextData>({
    title: 'Home',
    showSearch: false,
    searchOptions: [],
    showSearchOptions: false,
    updateSearchOn: 'blur',
    searchValue: '',
  });

  const open = drawerOpen ?? !isMobile;

  //full height layout
  useLayoutEffect(() => {
    const handleResizeList = () => {
      // set list height
      let header = document.getElementById('listHeader');
      let rectHeader = header?.getBoundingClientRect();

      let list = document.getElementById('mainList');
      if (list) {
        list.style.height = window.innerHeight - (rectHeader?.bottom ?? 0) + 'px';
      }
    };

    const handleResizeContent = () => {
      let mainHeader = document.getElementById('navbarHeader');
      let rectMainHeader = mainHeader?.getBoundingClientRect();

      let content = document.getElementById('drawerContent');
      if (content) {
        content.style.height = window.innerHeight - (rectMainHeader?.bottom ?? 0) + 'px';
      }
    };

    if (!isMobile && props.isAuthenticated) {
      window.addEventListener('resize', handleResizeList);
      handleResizeList();
    }

    if (isMobile) {
      window.addEventListener('resize', handleResizeContent);
      handleResizeContent();
    } else {
      let content = document.getElementById('drawerContent');
      if (content) {
        content.style.removeProperty('height');
      }
    }

    return () => {
      window.removeEventListener('resize', handleResizeList);
      window.removeEventListener('resize', handleResizeContent);
    };
  }, [isMobile, props.isAuthenticated]);

  useEffect(() => {
    if (!props.isAuthenticated) setDrawerOpen(false);
  }, [props.isAuthenticated]);

  const goToLocation = (location) => {
    props.history.push(location);
    if (isMobile) setDrawerOpen(false);
  };

  const drawerItems = [
    { divider: true, subHeaderText: 'TIPS', hideDivider: true },
    { text: 'Dashboard', icon: <Dashboard />, location: '/', hidden: employeeInfo?.hasClaim('DisableDashboard', 'true') },
    {
      text: 'Timesheet',
      icon: <Timer />,
      location: '/Timesheet',
      hidden: (employeeInfo?.hasClaim('DisableTimesheet', 'true') || employeeInfo?.hasClaim('department', 'shop')) && !employeeInfo?.isInRole('Admin'),
    },
    {
      text: 'Shop Timesheet',
      icon: <Timer />,
      location: '/ShopTimesheet',
      hidden:
        employeeInfo?.hasClaim('DisableTimesheet', 'true') ||
        (!employeeInfo?.hasClaim('department', 'shop') && !employeeInfo?.hasClaim('department', 'Shop Manager') && !employeeInfo?.isInRole('Admin')),
    },
    {
      text: 'Shop Clock',
      icon: <Timer />,
      location: '/ShopClock',
      hidden: !employeeInfo?.hasClaim('department', 'Shop Clock') && !employeeInfo?.hasClaim('department', 'Shop Manager') && !employeeInfo?.isInRole('Admin'),
    },
    { text: 'Expenses', icon: <AttachMoney />, location: '/Expenses', hidden: employeeInfo?.hasClaim('DisableExpenses', 'true') && !employeeInfo?.isInRole('Admin') },
    {
      text: 'Company Card',
      icon: <CreditCard />,
      location: '/CreditCards',
      hidden: (employeeInfo?.creditCards?.length ?? 0) === 0 && !employeeInfo?.isInRoles(['Accounting', 'Admin']),
    },
    { text: 'My Schedule', icon: <Today />, location: '/MySchedule', hidden: !employeeInfo?.hasSkillClaim() && !employeeInfo?.isInRole('Admin') },
    {
      text: 'Shop Dashboard',
      icon: <BusinessCenter />,
      location: '/Shop',
      hidden: !employeeInfo?.hasClaim('Department', 'Shop') && !employeeInfo?.hasClaim('Department', 'Shop Manager') && !employeeInfo?.isInRoles(['Accounting', 'Admin']),
    },
    {
      text: 'PO Dashboard',
      icon: <LocalShipping />,
      location: '/PODashboard',
      hidden:
        !employeeInfo?.hasClaim('Department', 'Shop') && !employeeInfo?.hasClaim('Department', 'Shop Manager') && !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']),
    },

    { text: 'Approvals', icon: <CheckBox />, location: '/Approvals', hidden: !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']) },
    {
      text: 'Files',
      icon: <PermMedia />,
      onClick: () => props.history.push(location.pathname + location.search, { ...(location.state as any), dialogType: 'files' }),
      hidden: employeeInfo?.hasClaim('DisableFiles', 'true') && !employeeInfo?.isInRole('Admin'),
    },
    { divider: true, subHeaderText: 'PROJECTS', hidden: !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']) },
    { text: 'Scheduler', icon: <EventIcon />, location: '/SchedulerDashboard', hidden: !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']) },
    { text: 'Manage Jobs', icon: <Assignment />, location: '/JobDashboard', hidden: !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']) },
    {
      text: 'SaleOrders ',
      icon: <LocalAtm />,
      location: '/SoDashboard',
      hidden:
        !employeeInfo?.hasClaim('Department', 'Shop') && !employeeInfo?.hasClaim('Department', 'Shop Manager') && !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']),
    },
    { text: 'Job Costing', icon: <BarChart />, location: '/JobCosting', hidden: !employeeInfo?.isInRoles(['Manager', 'Accounting', 'Admin']) },
    { divider: true, subHeaderText: 'ACCOUNTING', hidden: !employeeInfo?.isInRoles(['Admin', 'Accounting']) },
    { text: 'Dashboard', icon: <AccountBalance />, location: '/AccountingDashboard', hidden: !employeeInfo?.isInRoles(['Admin', 'Accounting']) },
    { text: 'Bill Rates', icon: <Receipt />, location: '/BillRates', hidden: !employeeInfo?.isInRoles(['Admin', 'Accounting']) },
    { divider: true, subHeaderText: 'ADMIN', hidden: !employeeInfo?.isInRole('Admin') },
    { text: 'User Management', icon: <People />, location: '/ManageUsers', hidden: !employeeInfo?.isInRole('Admin') },
  ];

  return (
    <AppBarContext.Provider value={[appBarData, setAppBarData]}>
      <div className={classes.grow}>
        <AppBar
          position='sticky'
          className={clsx(classes.appBar, {
            [classes.appBarShift]: open,
          })}
          elevation={0}
          id='navbarHeader'
        >
          <Toolbar>
            {props.isAuthenticated ? (
              <IconButton edge='start' color='inherit' aria-label='menu' onClick={() => setDrawerOpen((open) => !open)}>
                <MenuIcon />
              </IconButton>
            ) : null}
            <IconButton edge='start' color='inherit' aria-label='menu' onClick={() => props.history.push(employeeInfo?.getClaimValue('defaultHome') ?? '/')}>
              <Home />
            </IconButton>
            <Typography className={classes.pageTitle}>{appBarData.title}</Typography>
            {props.isAuthenticated ? <Typography className={classes.userName}>{!isMobile ? employeeInfo?.userName : ''}</Typography> : null}
            <IconButton
              edge='end'
              aria-label='settings'
              aria-controls={'settings-dialog'}
              aria-haspopup='true'
              onClick={() => props.history.push(location.pathname + location.search, { ...(location.state as any), dialogType: 'settings' })}
              color='inherit'
              className={clsx(classes.rightMenuItem, !props.isAuthenticated ? classes.rightMenuMarginAuto : '')}
            >
              <Settings />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          open={open}
          onClose={() => setDrawerOpen(false)}
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor='left'
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{ keepMounted: true, disablePortal: true }}
        >
          <div id={'listHeader'} className={classes.drawerHeader}>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <ChevronLeft className={classes.secondary} />
            </IconButton>
          </div>
          <Divider />
          {props.isAuthenticated ? (
            <List id='mainList' className={classes.mainList}>
              <SidebarList list={drawerItems} goToLocation={goToLocation} location={location} />
            </List>
          ) : null}
        </Drawer>
        <div
          id={'drawerContent'}
          className={clsx(classes.content, {
            [classes.contentShift]: open,
          })}
        >
          <main className={clsx('MainContent', open ? 'noscroll' : '')}>{props.children}</main>
          {employeeInfo && filesOpen ? <FileDialog id='navbar' open={filesOpen} employeeInfo={employeeInfo} toast={toast} onClose={() => props.history.goBack()} /> : null}
          <SettingsDialog
            open={settingsOpen}
            employeeInfo={employeeInfo}
            toast={toast}
            onClose={() => props.history.goBack()}
            handleLogout={props.handleLogout}
            toggleTheme={props.toggleTheme}
            isAuthenticated={props.isAuthenticated}
          />
        </div>
      </div>
    </AppBarContext.Provider>
  );
}
